<template>
	<div class="bar-chart-container">
		<BarChartGenerator
			:chart-options="enhancedChartOptions"
			:chart-data="paginatedChartData"
			:chart-id="chartId"
			:dataset-id-key="datasetIdKey"
			:plugins="plugins"
			:css-classes="cssClasses"
			:styles="styles"
			:width="width"
			:height="adjustedHeight"
		/>
		<!-- Legend Pagination Controls -->
		<div
			v-if="showPaginationControls"
			class="legend-pagination-controls mt-2"
		>
			<div class="d-flex align-center justify-space-between">
				<v-btn
					:disabled="currentLegendPage === 0"
					small
					text
					@click="previousLegendPage"
				>
					<v-icon left>
						mdi-chevron-left
					</v-icon>
					Previous
				</v-btn>
				<span class="legend-page-info">
					Showing {{ currentPageStart + 1 }}-{{ currentPageEnd }} of {{ totalDatasets }} datasets
				</span>
				<v-btn
					:disabled="currentLegendPage >= totalLegendPages - 1"
					small
					text
					@click="nextLegendPage"
				>
					Next
					<v-icon right>
						mdi-chevron-right
					</v-icon>
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import { Bar as BarChartGenerator } from 'vue-chartjs/legacy'
import {
	Chart as ChartJS,
	Title,
	Tooltip,
	LinearScale,
	CategoryScale,
	BarElement,
	Legend
} from 'chart.js'
import { getChartColorByIndex } from '../../utils/getChartColorByIndex.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

export default {
	name: 'bar-chart',
	components: {
		BarChartGenerator
	},
	props: {
		chartId: {
			type: String,
			default: 'bar-chart'
		},
		datasetIdKey: {
			type: String,
			default: 'label'
		},
		width: {
			type: Number,
			default: 400
		},
		height: {
			type: Number,
			default: 400
		},
		cssClasses: {
			default: '',
			type: String
		},
		styles: {
			type: Object,
			default: () => {}
		},
		plugins: {
			type: Array,
			default: () => []
		},
		chartData: {
			type: Object
		},
		chartOptions: {
			type: Object
		},
		palette: {
			type: String,
			default: 'default'
		},
		colorVersion: {
			type: Number,
			default: 0
		},
		legendItemsPerPage: {
			type: Number,
			default: 5
		}
	},
	data () {
		return {
			currentLegendPage: 0
		}
	},
	computed: {
		totalDatasets () {
			return this.chartData?.datasets?.length || 0
		},
		showPaginationControls () {
			return this.totalDatasets > this.legendItemsPerPage
		},
		totalLegendPages () {
			return Math.ceil(this.totalDatasets / this.legendItemsPerPage)
		},
		currentPageStart () {
			return this.currentLegendPage * this.legendItemsPerPage
		},
		currentPageEnd () {
			return Math.min(this.currentPageStart + this.legendItemsPerPage, this.totalDatasets)
		},
		adjustedHeight () {
			// Subtract pagination controls height when they're visible
			const paginationHeight = this.showPaginationControls ? 40 : 0
			return this.height - paginationHeight
		},
		sortedDatasets () {
			if (!this.chartData?.datasets) return []

			// Calculate total values for each dataset to determine "top" results
			const datasetsWithTotal = this.chartData.datasets.map((dataset, index) => {
				const total = dataset.data.reduce((sum, value) => sum + (value || 0), 0)
				return {
					...dataset,
					originalIndex: index,
					totalValue: total
				}
			})

			// Sort by total value descending (top results first)
			return datasetsWithTotal.sort((a, b) => b.totalValue - a.totalValue)
		},
		paginatedChartData () {
			// Reference colorVersion to trigger reactivity when custom colours change
			// eslint-disable-next-line no-unused-vars
			const _version = this.colorVersion

			if (!this.chartData) return null

			let sourceDatasets = this.chartData.datasets || []

			// Apply pagination if needed
			if (this.showPaginationControls) {
				const startIndex = this.currentPageStart
				const endIndex = this.currentPageEnd
				sourceDatasets = this.sortedDatasets.slice(startIndex, endIndex)
			}

			// Apply colors in exact order from chart-colors.js palette
			// Use the global index to continue cycling colors across pagination
			const enhancedDatasets = sourceDatasets.map((dataset, pageIndex) => {
				const newDataset = { ...dataset }
				// Calculate global index: start of current page + position within page
				const globalIndex = this.showPaginationControls
					? (this.currentPageStart + pageIndex)
					: pageIndex
				const color = getChartColorByIndex(globalIndex, this.palette)

				// Apply color to dataset
				newDataset.backgroundColor = color
				newDataset.borderColor = color

				return newDataset
			})

			return {
				...this.chartData,
				datasets: enhancedDatasets
			}
		},
		enhancedChartOptions () {
			const isDark = this.$vuetify?.theme?.dark || false
			const gridColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
			const tickColor = isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
			const legendColor = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)'

			const baseOptions = {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					x: {
						ticks: {
							color: tickColor,
							font: { size: 11 }
						},
						grid: {
							display: false
						},
						border: {
							display: false
						}
					},
					y: {
						beginAtZero: true,
						ticks: {
							color: tickColor,
							font: { size: 11 }
						},
						grid: {
							display: true,
							color: gridColor,
							drawBorder: false
						},
						border: {
							display: false
						}
					}
				},
				elements: {
					bar: {
						borderRadius: 4
					}
				},
				plugins: {
					legend: {
						position: 'bottom',
						labels: {
							usePointStyle: true,
							pointStyle: 'circle',
							padding: 12,
							color: legendColor,
							font: {
								size: 11
							},
							boxWidth: 10,
							boxHeight: 10
						}
					},
					tooltip: {
						backgroundColor: 'rgba(0, 0, 0, 0.9)',
						titleColor: 'white',
						bodyColor: 'white',
						borderColor: 'rgba(255, 255, 255, 0.2)',
						borderWidth: 1,
						cornerRadius: 6,
						displayColors: true,
						usePointStyle: true
					}
				}
			}

			// Deep merge with user-provided options
			return this.deepMerge(baseOptions, this.chartOptions || {})
		}
	},
	created () {
		// Set global font defaults for Chart.js
		ChartJS.defaults.font.family = 'Inter, sans-serif'
		ChartJS.defaults.font.size = 12
		ChartJS.defaults.font.weight = '400'
	},
	methods: {
		nextLegendPage () {
			if (this.currentLegendPage < this.totalLegendPages - 1) {
				this.currentLegendPage++
			}
		},
		previousLegendPage () {
			if (this.currentLegendPage > 0) {
				this.currentLegendPage--
			}
		},
		deepMerge (target, source) {
			const result = { ...target }
			for (const key in source) {
				if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
					result[key] = this.deepMerge(result[key] || {}, source[key])
				} else {
					result[key] = source[key]
				}
			}
			return result
		}
	}
}
</script>

<style scoped>
.bar-chart-container {
	width: 100%;
}

.legend-pagination-controls {
	border-top: 1px solid var(--global-border-color);
	padding-top: 4px;
	margin-top: 4px;
}

.legend-page-info {
	font-size: 12px;
	color: var(--global-foreground);
	font-weight: 500;
}
</style>
